import { useEffect, useState } from "react";
import { Alert, Autocomplete, Stack, TextField } from "@mui/material";
import { Visibility } from "@mui/icons-material";
import ButtonOne from "../../../../common/normal-button";
import { TEMPLATE_NAMES_ENDPOINT } from "../../../../config/urls";
import { useOtpApi } from "../../otpApi";
import OtpTemplatePreview from "./OtpTemplatePreview";
import { HelperText, MappingRow, ReadOnlyField, SectionTitle, SubSection } from "./OtpPanelParts";

const AUTH_TEMPLATES_URL = `${TEMPLATE_NAMES_ENDPOINT}?category_filter=AUTHENTICATION&status_filter=APPROVED&limit=1000`;

const OtpSelectTemplatePreset = ({ config, handleConfigChange }) => {
  const callApi = useOtpApi();
  const [authTemplates, setAuthTemplates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    callApi(AUTH_TEMPLATES_URL, { method: "GET" })
      .then(({ body }) => setAuthTemplates(body?.results || []))
      .catch((error) => console.error("Failed to load templates", error))
      .finally(() => setLoaded(true));
  }, [callApi]);

  const selected = authTemplates.find(
    (template) => template.template_name === config.template_name,
  );
  const variables = config.variables || {};

  const selectTemplate = (template) => {
    handleConfigChange("template_name", template?.template_name);
    handleConfigChange("variables", {});
  };

  const setVariable = (key, value) =>
    handleConfigChange("variables", { ...variables, [key]: value });

  return (
    <Stack spacing={3}>
      <Autocomplete
        options={authTemplates}
        value={selected || null}
        onChange={(event, template) => selectTemplate(template)}
        getOptionLabel={(option) =>
          `${option.template_name}${option.template_json?.languageCode ? ` (${option.template_json.languageCode})` : ""}`
        }
        isOptionEqualToValue={(option, value) => option.template_name === value.template_name}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label="OTP Template"
            helperText="Only approved templates in the Authentication category are listed."
          />
        )}
        noOptionsText="No approved Authentication templates"
      />

      {loaded && config.template_name && !selected && (
        <Alert severity="warning">
          "{config.template_name}" is no longer an approved Authentication template. Pick another
          one before activating this flow.
        </Alert>
      )}

      {selected && (
        <Stack spacing={2}>
          <SectionTitle>Template Variables</SectionTitle>
          <SubSection>
            {(selected.variables || []).map((variable) => {
              const name = variable.key === "button" ? "Button" : `{{${variable.key}}}`;
              return (
                <MappingRow key={variable.key} name={name} arrow="→">
                  {variable.type === "system" ? (
                    <ReadOnlyField label="Mapped to" value="OTP code (auto-filled)" />
                  ) : (
                    <TextField
                      required
                      size="small"
                      label="Custom value"
                      value={variables[variable.key] || ""}
                      onChange={(event) => setVariable(variable.key, event.target.value)}
                    />
                  )}
                </MappingRow>
              );
            })}
            <HelperText>
              System variables fill in automatically. Only custom variables take a value: plain
              text or a flow variable like {"{{brand_name}}"}.
            </HelperText>
          </SubSection>
          <ButtonOne
            variant="outlined"
            title="Preview Template"
            startIcon={<Visibility />}
            onClick={() => setPreviewOpen(true)}
            sx={{ alignSelf: "flex-start" }}
          />
        </Stack>
      )}

      <OtpTemplatePreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        template={selected}
        variables={variables}
      />
    </Stack>
  );
};

export default OtpSelectTemplatePreset;
